import React, { useEffect, useRef, useState } from "react";
import { tv } from "tailwind-variants";

interface DropdownOption {
    label: string;
    value: string;
    href?: string;
    onClick?: () => void;
    disabled?: boolean;
    icon?: React.ReactNode;
    separator?: boolean;
}

interface DropdownProps {
    buttonLabel?: string;
    isOpen: boolean;
    toggleDropdown?: () => void;
    onClose?: () => void;
    options?: DropdownOption[];
    actionComponent?: React.ReactNode;
    children?: React.ReactNode;
    placement?: "bottom-left" | "bottom-right" | "top-left" | "top-right";
    size?: "small" | "medium" | "large";
    variant?: "default" | "outlined" | "ghost";
    fullWidth?: boolean;
    className?: string;
    menuClassName?: string;
    closeOnClick?: boolean;
}

const dropdownButton = tv({
    base:
        "inline-flex items-center justify-center font-semibold rounded-[var(--radius-md)] transition-all duration-[var(--duration-normal)] ease-[var(--ease-calm)] tracking-[-0.012em] " +
        "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--primary-500)]/25",
    variants: {
        size: {
            small: "text-[var(--font-secondary)] px-3.5 py-2 gap-1.5",
            medium: "text-[var(--font-body)] px-4.5 py-2.5 gap-2",
            large: "text-base px-6 py-3 gap-2",
        },
        variant: {
            default:
                "bg-gradient-to-b from-[var(--primary-500)] to-[var(--primary-600)] text-white border border-[var(--primary-700)]/30 shadow-[0_1px_3px_rgba(4,94,184,0.25),inset_0_1px_0_rgba(255,255,255,0.12)] hover:from-[var(--primary-600)] hover:to-[var(--primary-700)] hover:shadow-[0_2px_6px_rgba(4,94,184,0.35)]",
            outlined:
                "border border-[var(--border-subtle)] hover:border-[var(--border-glass-strong)] shadow-[0_1px_2px_rgba(0,0,0,0.06)]",
            ghost:
                "bg-transparent hover:bg-[var(--bg-hover)]",
        },
        fullWidth: { true: "w-full" },
    },
    defaultVariants: { size: "medium", variant: "default" },
});

const placements: Record<string, string> = {
    "bottom-left": "top-full left-0 mt-2",
    "bottom-right": "top-full right-0 mt-2",
    "top-left": "bottom-full left-0 mb-2",
    "top-right": "bottom-full right-0 mb-2",
};

const Dropdown: React.FC<DropdownProps> = ({
    buttonLabel = "Dropdown",
    isOpen,
    options,
    toggleDropdown,
    onClose,
    actionComponent,
    children,
    placement = "bottom-left",
    size = "medium",
    variant = "default",
    fullWidth = false,
    className = "",
    menuClassName = "",
    closeOnClick = true,
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose?.();
            }
        };

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose?.();
            if (!options?.length) return;
            if (event.key === "ArrowDown") setFocusedIndex((p) => Math.min(options.length - 1, p + 1));
            if (event.key === "ArrowUp") setFocusedIndex((p) => Math.max(0, p - 1));
            if (event.key === "Enter" && focusedIndex >= 0) handleOptionClick(options[focusedIndex]);
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("keydown", handleKey);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKey);
        };
    }, [isOpen, options, focusedIndex, onClose]);

    const handleOptionClick = (option: DropdownOption) => {
        if (option.disabled) return;
        if (option.onClick) option.onClick();
        else if (option.href) window.location.href = option.href;
        if (closeOnClick) onClose?.();
    };

    return (
        <div ref={dropdownRef} className={`relative ${fullWidth ? "w-full" : "inline-block"} ${className}`}>
            {actionComponent ? (
                <div className={fullWidth ? "w-full" : ""}>{actionComponent}</div>
            ) : (
                <button
                    onClick={toggleDropdown}
                    className={dropdownButton({ size, variant, fullWidth })}
                    style={
                        variant === 'outlined'
                            ? { background: 'var(--bg-card)', color: 'var(--text-primary)' }
                            : variant === 'ghost'
                                ? { color: 'var(--text-secondary)' }
                                : undefined
                    }
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                >
                    {buttonLabel}
                    <svg
                        className={`w-4 h-4 ml-2 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            )}

            {isOpen && (
                <div
                    className={`
            absolute z-50 rounded-[var(--radius-lg)] border
            min-w-52 max-w-sm py-2 animate-in fade-in zoom-in-95 duration-[var(--duration-fast)] backdrop-blur-xl
            ${placements[placement]} ${fullWidth ? "w-full" : "w-max"} ${menuClassName}
          `}
                    style={{
                        background: 'var(--bg-elevated, var(--bg-card))',
                        borderColor: 'var(--border-subtle)',
                        color: 'var(--text-primary)',
                        boxShadow: 'var(--shadow-premium, 0 8px 32px rgba(0,0,0,0.28))',
                    }}
                    role="menu"
                >
                    {options ? (
                        <div className="max-h-72 overflow-y-auto custom-scrollbar px-1.5">
                            {options.map((option, index) => (
                                <React.Fragment key={`${option.value}-${index}`}>
                                    {option.separator && index > 0 && <div className="h-px my-1.5 mx-2" style={{ background: 'var(--border-divider)' }} />}
                                    <button
                                        onClick={() => handleOptionClick(option)}
                                        disabled={option.disabled}
                                        className={`
                      w-full text-left px-3.5 py-2.5 text-[13px] font-medium
                      transition-all duration-[var(--duration-fast)] ease-[var(--ease-calm)]
                      flex items-center gap-3 rounded-[var(--radius-md)]
                    `}
                                        style={{
                                            color: option.disabled
                                                ? 'var(--text-muted)'
                                                : 'var(--text-primary)',
                                            background: focusedIndex === index
                                                ? 'var(--bg-hover)'
                                                : 'transparent',
                                            cursor: option.disabled ? 'not-allowed' : 'pointer',
                                        }}
                                        onMouseEnter={!option.disabled ? (e) => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-hover)'; } : undefined}
                                        onMouseLeave={!option.disabled ? (e) => { (e.currentTarget as HTMLElement).style.background = focusedIndex === index ? 'var(--bg-hover)' : 'transparent'; } : undefined}
                                        role="menuitem"
                                    >
                                        {option.icon && <span className="w-5 h-5 flex-shrink-0">{option.icon}</span>}
                                        <span className="flex-1 truncate">{option.label}</span>
                                    </button>
                                </React.Fragment>
                            ))}
                        </div>
                    ) : (
                        children && <div>{children}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Dropdown;
